/**
 * ContentPageHeader Component
 *
 * A page header for content pages with title and optional description.
 * Used for pages like Cut Activity that have a title with explanatory text.
 */

import React from 'react';
import { Box, Typography, Link as MuiLink, useMediaQuery, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { colors, typography as typographyTokens, spacing } from '../../tokens';

export interface ContentPageHeaderProps {
    /** Page title */
    title: string;
    /** Description text (can include inline elements) */
    description?: React.ReactNode;
    /** Additional styles */
    sx?: SxProps<Theme>;
}

export const ContentPageHeader: React.FC<ContentPageHeaderProps> = ({ title, description, sx }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    return (
    <Box
        sx={{
                mb: isMobile ? 2 : 3,
            flexShrink: 0,
            ...sx,
        }}>
        <Typography
            variant='h1'
            sx={{
                fontFamily: typographyTokens.fontFamily.display,
                fontWeight: typographyTokens.fontWeight.bold,
                    fontSize: isMobile ? typographyTokens.fontSize.xl : typographyTokens.fontSize['2xl'],
                color: colors.neutral[900],
                lineHeight: 1.3,
                mb: description ? 1 : 0,
            }}>
            {title}
        </Typography>

            {description && !isMobile && (
            <Typography
                variant='body2'
                sx={{
                    color: colors.neutral[600],
                    fontSize: typographyTokens.fontSize.sm,
                    lineHeight: 1.5,
                    maxWidth: 700,
                    '& a': {
                        color: colors.primary[500],
                        textDecoration: 'none',
                        fontWeight: typographyTokens.fontWeight.medium,
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    },
                }}>
                {description}
            </Typography>
        )}
    </Box>
);
};

export default ContentPageHeader;
